const isEnabled = (value: string | undefined) => value === "true" || value === "1"

export const FEATURE_FLAG_STORAGE_KEY = "dc-ireland-feature-flag-overrides"

export type FeatureFlagKey = "adminSignup" | "announcements" | "stripeGiving"

export type FeatureFlags = Record<FeatureFlagKey, boolean>

export const FEATURE_FLAG_META: Record<
  FeatureFlagKey,
  { label: string; description: string; envKey?: string }
> = {
  adminSignup: {
    label: "Admin signup",
    description: "Shows a Sign up button on the admin login page.",
  },
  announcements: {
    label: "News & Announcements",
    description: "Shows the News page, announcements admin section, and nav link.",
    envKey: "VITE_ENABLE_ANNOUNCEMENTS",
  },
  stripeGiving: {
    label: "Stripe Giving",
    description: "Shows the card giving option on the Give page.",
    envKey: "VITE_ENABLE_STRIPE_GIVING",
  },
}

const envDefaults: FeatureFlags = {
  adminSignup: false,
  announcements: isEnabled(import.meta.env.VITE_ENABLE_ANNOUNCEMENTS),
  stripeGiving: isEnabled(import.meta.env.VITE_ENABLE_STRIPE_GIVING),
}

export const getBuildFeatureFlags = (): FeatureFlags => ({ ...envDefaults })

type FeatureFlagOverrides = Partial<Record<FeatureFlagKey, boolean>>

const readOverrides = (): FeatureFlagOverrides => {
  if (typeof window === "undefined") return {}

  try {
    const raw = window.localStorage.getItem(FEATURE_FLAG_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as FeatureFlagOverrides
    return parsed && typeof parsed === "object" ? parsed : {}
  } catch {
    return {}
  }
}

export const getFeatureFlags = (): FeatureFlags => {
  const overrides = readOverrides()
  return {
    adminSignup: overrides.adminSignup ?? envDefaults.adminSignup,
    announcements: overrides.announcements ?? envDefaults.announcements,
    stripeGiving: overrides.stripeGiving ?? envDefaults.stripeGiving,
  }
}

export const getFeatureFlagOverrides = (): FeatureFlagOverrides => readOverrides()

export const setFeatureFlagOverride = (key: FeatureFlagKey, enabled: boolean) => {
  const overrides = readOverrides()
  overrides[key] = enabled
  window.localStorage.setItem(FEATURE_FLAG_STORAGE_KEY, JSON.stringify(overrides))
  window.dispatchEvent(new CustomEvent("dc-feature-flags-changed"))
}

export const clearFeatureFlagOverrides = () => {
  window.localStorage.removeItem(FEATURE_FLAG_STORAGE_KEY)
  window.dispatchEvent(new CustomEvent("dc-feature-flags-changed"))
}

export const isStripeGivingEnabled = () => getFeatureFlags().stripeGiving
